import React, { useState } from 'react';
import { useWBL } from '../../context/WBLContext';
import { HeroBanner } from '../Common/HeroBanner';
import { 
  Mail, MailOpen, Trash2, Search, Send, User, 
  ChevronDown, ChevronUp, Inbox, RefreshCw, 
  AlertCircle, CheckCircle, Star, Square, CheckSquare, 
  MoreVertical, Filter, Archive, Clock, Info, ShieldCheck
} from 'lucide-react';
import { getRegionDarkBg, getRegionThemeAccent } from '../../utils/regionUtils';
import { motion, AnimatePresence } from 'motion/react';

export const MessagesPage: React.FC = () => {
  const { 
    messages, 
    activeUser, 
    activeTenant,
    globalBranding,
    tenants,
    markMessageAsRead, 
    markMessageAsUnread, 
    deletePortalMessage
  } = useWBL();

  // Filter out soft deleted messages
  const activeMessages = (messages || []).filter(msg => !msg.deleted);

  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Filters
  const filteredMessages = activeMessages.filter(msg => {
    const matchSearch = 
      (msg.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (msg.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (msg.toEmail || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (msg.bodyHtml || '').toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchSearch) return false;

    if (filter === 'unread') return !msg.read;
    if (filter === 'read') return msg.read;

    return true;
  });

  const toggleExpand = (id: string) => {
    if (expandedMessageId === id) {
      setExpandedMessageId(null);
    } else {
      setExpandedMessageId(id);
      const msg = activeMessages.find(m => m.id === id);
      if (msg && !msg.read) {
        markMessageAsRead(id);
      }
    }
  };

  const handleToggleRead = async (e: React.MouseEvent, msg: any) => {
    e.stopPropagation();
    if (msg.read) {
      await markMessageAsUnread(msg.id);
    } else {
      await markMessageAsRead(msg.id);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedIds(prev => prev.filter(i => i !== id));
    if (expandedMessageId === id) {
      setExpandedMessageId(null);
    }
    await deletePortalMessage(id);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    const idsToDelete = [...selectedIds];
    setSelectedIds([]);
    if (expandedMessageId && idsToDelete.includes(expandedMessageId)) {
      setExpandedMessageId(null);
    }
    await Promise.all(idsToDelete.map(id => deletePortalMessage(id)));
  };

  const handleMarkSelectedRead = async () => {
    if (selectedIds.length === 0) return;
    await Promise.all(selectedIds.map(id => markMessageAsRead(id)));
  };

  const handleMarkSelectedUnread = async () => {
    if (selectedIds.length === 0) return;
    await Promise.all(selectedIds.map(id => markMessageAsUnread(id)));
  };

  const toggleSelect = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredMessages.length && filteredMessages.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredMessages.map(m => m.id));
    }
  };

  return (
    <div className="space-y-6 pb-12 flex-1 flex flex-col min-h-0">
      <HeroBanner 
        title="Messages"
        subtitle="Manage your system messages and platform dispatches"
        pageKey="messages"
      />

      <div className="w-[95%] max-w-[95%] mx-auto flex-1 flex flex-col min-h-0 bg-white rounded-3xl border border-slate-200 shadow-md overflow-hidden" id="messages-gmail-container">
      {/* Gmail Top Bar */}
      <div className="flex flex-col md:flex-row items-center gap-4 px-6 py-4 border-b border-slate-100 bg-white sticky top-0 z-20 shadow-sm">
        
        {/* Search Bar - Gmail style */}
        <div className="flex-1 max-w-2xl relative w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search mail"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-slate-100/50 border-none rounded-2xl focus:bg-white focus:shadow-md focus:ring-0 outline-none text-sm transition-all"
          />
        </div>
      </div>

      {/* Inbox View - Gmail row style */}
      <div className="flex-1 overflow-y-auto flex flex-col min-h-0 bg-white">
        {/* Inbox Controls bar */}
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white/90 backdrop-blur-md z-10">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleSelectAll}
              title={selectedIds.length === filteredMessages.length && filteredMessages.length > 0 ? "Deselect All" : "Select All"}
              className="text-slate-400 hover:text-slate-600 transition-colors p-1 cursor-pointer"
            >
              {selectedIds.length === filteredMessages.length && filteredMessages.length > 0 ? (
                <CheckSquare className="w-5 h-5 text-[#0094c8]" />
              ) : (
                <Square className="w-5 h-5" />
              )}
            </button>
            
            <div className="h-4 w-px bg-slate-200 mx-1" />
            
            <div className="flex items-center gap-1">
              <button 
                onClick={() => { setSelectedIds([]); setSearchTerm(''); }}
                title="Refresh View"
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {selectedIds.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-1 border-l border-slate-200 pl-4 ml-2"
              >
                <button 
                  onClick={handleDeleteSelected}
                  title="Delete Selected Messages"
                  className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer flex items-center gap-1"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-xs font-bold text-rose-600 hidden sm:inline">Delete</span>
                </button>
                <button 
                  onClick={handleMarkSelectedRead}
                  title="Mark as Read"
                  className="p-2 text-slate-400 hover:text-[#0094c8] hover:bg-sky-50 rounded-lg transition-all cursor-pointer"
                >
                  <MailOpen className="w-4 h-4" />
                </button>
                <button 
                  onClick={handleMarkSelectedUnread}
                  title="Mark as Unread"
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                >
                  <Mail className="w-4 h-4" />
                </button>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                  {selectedIds.length} Selected
                </span>
              </motion.div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
              {filteredMessages.length} Conversations
            </span>
            <div className="flex items-center gap-1 ml-4">
              {(['all', 'unread', 'read'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${
                    filter === f ? 'bg-slate-900 text-white' : 'text-slate-400 hover:bg-slate-100'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* List of Messages */}
        <div className="divide-y divide-slate-100">
          {filteredMessages.length === 0 ? (
            <div className="py-24 flex flex-col items-center justify-center text-center px-4 animate-in fade-in duration-500">
              <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-6">
                <Inbox className="w-12 h-12 text-slate-200" />
              </div>
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Inbox Zero</h3>
              <p className="text-sm text-slate-400 mt-2 max-w-[320px] leading-relaxed font-medium">
                {filter === 'all' ? "Your communication stream is currently empty." : `No ${filter} messages found matching your search.`}
              </p>
            </div>
          ) : (
            filteredMessages.map((msg) => {
              const isExpanded = expandedMessageId === msg.id;
              const isSelected = selectedIds.includes(msg.id);
              const dateObj = msg.sentAt ? new Date(msg.sentAt) : new Date();
              const dateStr = dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              const bodyExcerpt = (msg.bodyHtml || '').replace(/<[^>]+>/g, ' ').trim();

              return (
                <div key={msg.id} className="group">
                  {/* Message Header Row */}
                  <div 
                    onClick={() => toggleExpand(msg.id)}
                    className={`flex items-center gap-4 px-6 py-3.5 cursor-pointer transition-all border-l-4 ${
                      isExpanded ? 'bg-slate-50 border-l-[#0094c8]' : 'hover:bg-slate-50/80 border-l-transparent'
                    } ${!msg.read ? 'bg-white' : 'bg-white'}`}
                  >
                    <button 
                      onClick={(e) => toggleSelect(e, msg.id)}
                      className="text-slate-300 hover:text-slate-400 transition-colors p-1 shrink-0"
                    >
                      {isSelected ? <CheckSquare className="w-5 h-5 text-[#0094c8]" /> : <Square className="w-5 h-5" />}
                    </button>
                    
                    <button className="text-slate-300 hover:text-amber-400 transition-colors shrink-0">
                      <Star className="w-4 h-4" />
                    </button>

                    <div className={`w-40 shrink-0 truncate text-xs ${!msg.read ? 'font-black text-slate-900' : 'font-medium text-slate-600'}`}>
                      {msg.fromName || 'WBL Workspace'}
                    </div>

                    <div className="flex-1 flex items-center gap-2 overflow-hidden">
                      <span className={`truncate text-xs ${!msg.read ? 'font-black text-slate-900' : 'font-bold text-slate-700'}`}>
                        {msg.subject || 'System Notification'}
                      </span>
                      <span className="text-slate-400 text-xs font-medium truncate opacity-70">
                        - {bodyExcerpt}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 shrink-0">
                      <div className="group-hover:hidden text-[10px] font-bold text-slate-400 tracking-tighter uppercase w-16 text-right">
                        {dateStr}
                      </div>
                      
                      <div className="hidden group-hover:flex items-center gap-2 w-16 justify-end">
                        <button 
                          onClick={(e) => handleToggleRead(e, msg)}
                          className="p-1.5 text-slate-400 hover:text-[#0094c8] hover:bg-white rounded-lg shadow-xs transition-all"
                        >
                          {msg.read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                        </button>
                        <button 
                          onClick={(e) => handleDelete(e, msg.id)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-white rounded-lg shadow-xs transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {/* Accordion Content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden bg-slate-50/50"
                      >
                        <div className="px-6 md:px-24 py-12 border-t border-slate-100">
                          {/* Message Detail View */}
                          <div className="max-w-4xl mx-auto space-y-8">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-slate-200 flex items-center justify-center text-slate-600 shadow-inner">
                                  <User className="w-6 h-6" />
                                </div>
                                <div>
                                  <h2 className="text-xl font-black text-slate-900 tracking-tight">{msg.fromName || 'WBL Portal System'}</h2>
                                  <p className="text-xs text-slate-400 font-medium">To: {msg.toEmail}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right hidden md:block">
                                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sent At</p>
                                  <p className="text-xs font-bold text-slate-700">
                                    {msg.sentAt ? new Date(msg.sentAt).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' }) : 'Portal Dispatch'}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => handleToggleRead(e, msg)}
                                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl flex items-center space-x-1.5 transition-all cursor-pointer"
                                  >
                                    {msg.read ? <Mail className="w-3.5 h-3.5" /> : <MailOpen className="w-3.5 h-3.5" />}
                                    <span>{msg.read ? 'Mark Unread' : 'Mark Read'}</span>
                                  </button>
                                  <button
                                    onClick={(e) => handleDelete(e, msg.id)}
                                    className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs font-bold rounded-xl flex items-center space-x-1.5 transition-all cursor-pointer border border-rose-200"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Branded Email Container */}
                            {(() => {
                              const msgTenant = tenants?.find(t => t.id === msg.tenantId || t.subdomain === msg.tenantId) || activeTenant;
                              const regionId = msgTenant?.id || 'spartanburg';
                              const darkBg = getRegionDarkBg(regionId);
                              const accentColor = getRegionThemeAccent(regionId);
                              const regionName = msgTenant?.name || 'WBL Portal';
                              const logoUrl = msgTenant?.logos?.navigation || globalBranding?.logos?.navigation || '';
                              const subdomain = msgTenant?.subdomain || 'spartanburg';

                              return (
                                <div className="bg-white rounded-[32px] border border-slate-200 shadow-2xl overflow-hidden max-w-3xl mx-auto ring-1 ring-slate-100">
                                  <div 
                                    className="px-8 py-12 text-center text-white relative overflow-hidden"
                                    style={{ backgroundColor: darkBg }}
                                  >
                                    {/* Decorative gradient overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
                                    
                                    {logoUrl ? (
                                      <img 
                                        src={logoUrl} 
                                        alt={`${regionName} Logo`} 
                                        className="max-h-12 mx-auto mb-4 block relative z-10 drop-shadow-md" 
                                      />
                                    ) : (
                                      <div className="text-3xl font-black tracking-tighter mb-2 font-sans relative z-10">
                                        {regionName.toUpperCase()} <span style={{ color: accentColor }}>WBL</span>
                                      </div>
                                    )}
                                    <h1 className="text-white/90 text-sm font-black uppercase tracking-[0.2em] font-sans relative z-10">
                                      {regionName}
                                    </h1>
                                  </div>
                                  
                                  <div className="h-1.5" style={{ backgroundColor: accentColor }} />
                                  
                                  <div className="p-10 md:p-14 bg-white">
                                    <h3 className="font-extrabold text-2xl text-slate-900 mb-8 tracking-tight leading-snug text-center max-w-xl mx-auto">
                                      {msg.title || msg.subject}
                                    </h3>

                                    <div 
                                      className="prose prose-slate max-w-none text-slate-600 text-base leading-relaxed space-y-6 [&_a]:text-[#0094c8] [&_a]:underline hover:[&_a]:text-[#0077a3] [&_a]:font-bold [&_a]:cursor-pointer [&_p]:mb-4"
                                      dangerouslySetInnerHTML={{ __html: msg.bodyHtml || '' }}
                                    />
                                    
                                    <div className="mt-12 pt-12 border-t border-slate-100 flex flex-col items-center gap-6">
                                      <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                          <Info className="w-5 h-5" />
                                        </div>
                                        <div className="text-left">
                                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Portal Dispatch</p>
                                          <p className="text-[11px] text-slate-500 font-bold max-w-[300px]">Securely verified notification from the WBL Workspace network.</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="bg-slate-50 p-8 border-t border-slate-100 text-center flex flex-col items-center gap-3">
                                    <div className="flex flex-wrap items-center justify-center gap-3">
                                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                                        <ShieldCheck className="w-4 h-4 text-emerald-500" />
                                        <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Verified Workspace Domain</span>
                                      </div>
                                    </div>
                                    <p className="text-[11px] text-slate-400 font-medium">Please do not reply directly to this automated system message.</p>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

